import React, { useState, useLayoutEffect, useMemo } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { getAmChartsLocale, translateMonthLabels } from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";

const AnalyticsByClientOrders = ({ item }) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();

  const processOrdersData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    
    return data.map(item => ({
      month: item.month || "",
      createdOrder: item.createdOrder || 0,
      completedOrder: item.completedOrder || 0,
      completionRate: item.createdOrder > 0 ? 
        Math.round((item.completedOrder / item.createdOrder) * 100) : 0
    }));
  };

  const processedData = useMemo(() => {
    const raw = processOrdersData(item);
    return translateMonthLabels(raw, currentLanguage);
  }, [item, currentLanguage]);

  useLayoutEffect(() => {
    let root = am5.Root.new("chartdiv-client-orders");
    root.setThemes([am5themes_Animated.new(root)]);

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    root._logo.dispose();

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelY: "zoomX",
      }),
    );

    if (isMobileView) {
      chart.set("paddingLeft", 1);
    }
    chart.set("wheelable", true);

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "month",
        groupData: true,
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 30,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      }),
    );
    xAxis.data.setAll(processedData);

    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
      behavior: "zoomX",
      xAxis: xAxis
    }));
    cursor.lineY.set("visible", false);

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
      rotation: 0,
      multiline: true,
      textAlign: "center",
      centerX: am5.percent(50),
      maxWidth: 50,
      oversizedBehavior: "wrap"
    });

    let yAxisLeft = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      }),
    );

    yAxisLeft.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxisLeft.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
    });

    yAxisLeft.children.unshift(
      am5.Label.new(root, {
        rotation: -90,
        text: t("analytics.ordersCreated"),
        y: am5.p50,
        centerX: am5.p50,
        centerY: am5.p50,
        fontSize: 14,
        fill: am5.color("#5A5959")
      })
    );

    let yAxisRight = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          opposite: true,
        }),
        min: 0,
        max: 100,
      }),
    );

    yAxisRight.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
    });

    yAxisRight.get("renderer").labels.template.adapters.add("text", function (text, target) {
      return text + "%";
    });

    yAxisRight.children.unshift(
      am5.Label.new(root, {
        rotation: -90,
        text: `% ${t("analytics.ordersAccepted")}`,
        y: am5.p50,
        x: 75,
        centerX: am5.p50,
        centerY: am5.p50,
        fontSize: 14,
        fill: am5.color("#5A5959"),
      })
    );

    yAxisRight.get("renderer").grid.template.setAll({
      visible: false,
    });

    const colors = {
      createdOrder: am5.color(0x4E7CFF),
      completedOrder: am5.color(0xFF98BB),
    };

    let createdOrderSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: t("analytics.ordersCreated"),
        xAxis: xAxis,
        yAxis: yAxisLeft,
        categoryXField: "month",
        valueYField: "createdOrder",
      }),
    );

    createdOrderSeries.columns.template.setAll({
      fill: colors.createdOrder,
      stroke: colors.createdOrder,
      maxWidth: 80,
    });

    createdOrderSeries.set("tooltip", am5.Tooltip.new(root, {
      labelText: "{name}: {valueY}",
    }));

    createdOrderSeries.data.setAll(processedData);

    let completionRateSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: t("analytics.accepted"),
        xAxis: xAxis,
        yAxis: yAxisRight,
        categoryXField: "month",
        fill: am5.color("#FF98BB"),
        valueYField: "completionRate",
        stroke: colors.completedOrder,
      }),
    );

    completionRateSeries.strokes.template.setAll({
      stroke: colors.completedOrder,
      strokeWidth: 3,
    });

    completionRateSeries.bullets.push(() => {
      return am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 4,
          fill: colors.completedOrder,
          stroke: am5.color("#FF98BB"),
          strokeWidth: 3,
        }),
      });
    });

    completionRateSeries.set("tooltip", am5.Tooltip.new(root, {
      labelText: "{name}: {valueY}%",
      fill: am5.color("#FF98BB"),
    }));

    completionRateSeries.data.setAll(processedData);

    return () => {
      root.dispose();
    };
  }, [item, isMobileView, processedData, currentLanguage]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{t("analytics.postedAndAcceptedShare")}</span>
      </div>

      <div className={cn(style.area__namesMobile)}>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: "#4E7CFF" }}></div>
          <div className={style.area_type}>{t("analytics.ordersCreated")}</div>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div 
            className={style.area_icon} 
            style={{ 
              backgroundColor: "#FF98BB",
              borderRadius: "2px",
            }}
          ></div>
          <div className={style.area_type}>{t("analytics.accepted")}</div>
        </div>
      </div>

      <div className={style.area}>
        <div id="chartdiv-client-orders" className={style.area_chart}></div>
        <div className={style.area_names}>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: "#4E7CFF" }}></div>
            <div className={style.area_type}>{t("analytics.ordersCreated")}</div>
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <svg width="44" height="14" viewBox="0 0 12 5" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="5.8088" cy="2.24727" r="1.96407" fill="#FF98BB" />
              <path
                d="M1.38965 1.75312C1.11847 1.75312 0.89863 1.97296 0.89863 2.24414C0.89863 2.51532 1.11847 2.73516 1.38965 2.73516V2.24414V1.75312ZM1.38965 2.24414V2.73516H11.701V2.24414V1.75312H1.38965V2.24414Z"
                fill="#FF98BB" />
            </svg>
            <div className={style.area_type}>{t("analytics.accepted")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsByClientOrders;
